// Wave AW.3-React (2026-05-16) — PROMISE (RFQ) 상세 화면
// API: GET /buyer-app/rfq/:id, POST /buyer-app/rfq/:id/quotes, DELETE quotes

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuctionBuyerQuoteModal from './AuctionBuyerQuoteModal';

function formatKrw(n) { if (n == null) return '-'; return Number(n).toLocaleString() + '원'; }

function getPhotoUrls(rfq) {
  var photos = rfq.photos;
  var urls = [];
  var origin = API.replace(/\/api$/, '');
  function toUrl(p) {
    if (!p) return null;
    if (/^https?:/.test(p)) return p;
    return origin + (p.charAt(0) === '/' ? p : '/' + p);
  }
  if (Array.isArray(photos)) {
    photos.forEach(function(p){ var u = toUrl(p); if (u) urls.push(u); });
  } else if (photos && typeof photos === 'object') {
    Object.keys(photos).forEach(function(k){ var u = toUrl(photos[k]); if (u) urls.push(u); });
  }
  // extras
  if (Array.isArray(rfq.extrasPhotos)) {
    rfq.extrasPhotos.forEach(function(p){ var u = toUrl(p); if (u) urls.push(u); });
  }
  return urls;
}

function AuctionBuyerPromiseDetail() {
  var navigate = useNavigate();
  var params = useParams();
  var rfqId = params.id;

  var [rfq, setRfq] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [quoteModalOpen, setQuoteModalOpen] = useState(false);
  var [photoIdx, setPhotoIdx] = useState(0);

  var load = useCallback(function(){
    setLoading(true); setError('');
    authBuyerFetch(API + '/buyer-app/rfq/' + rfqId, {})
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d){
        if (d && d.success && d.data) {
          setRfq(d.data);
        } else {
          setError((d && d.error) || 'PROMISE 를 찾을 수 없습니다');
        }
        setLoading(false);
      })
      .catch(function(e){
        if (e && e.message === 'unauthorized') return;
        setError('네트워크 오류'); setLoading(false);
      });
  }, [rfqId, navigate]);

  useEffect(function(){ load(); }, [load]);

  var handleWithdraw = function() {
    if (!rfq || !rfq.ownQuote) return;
    if (!window.confirm('시세를 철회하시겠습니까?\n(철회한 PROMISE 는 재제출 불가합니다)')) return;
    authBuyerFetch(API + '/buyer-app/rfq/' + rfq.id + '/quotes/' + rfq.ownQuote.id, { method: 'DELETE' })
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d){
        if (d && d.success) {
          load();
        } else {
          window.alert((d && d.error) || '철회 실패');
        }
      })
      .catch(function(e){ if (!(e && e.message === 'unauthorized')) window.alert('네트워크 오류'); });
  };

  if (loading) {
    return <AuctionBuyerLayout activeKey="promise"><div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>로딩 중...</div></AuctionBuyerLayout>;
  }
  if (error || !rfq) {
    return (
      <AuctionBuyerLayout activeKey="promise">
        <div style={{ textAlign: 'center', padding: 80 }}>
          <div style={{ color: '#dc2626', marginBottom: 16 }}>{error || 'PROMISE 를 찾을 수 없습니다'}</div>
          <button type="button" onClick={function(){navigate('/buyer/promise');}}
            style={{ padding: '10px 20px', background: '#1B3A5C', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: "'Pretendard',sans-serif" }}>
            목록으로
          </button>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var photoUrls = getPhotoUrls(rfq);
  var hasMyQuote = !!rfq.ownQuote;
  var myQuoteStatus = hasMyQuote ? rfq.ownQuote.status : null;
  var isActive = rfq.status === 'open';
  var canQuote = isActive && (!hasMyQuote || (myQuoteStatus === 'active' && (rfq.ownQuote.updateCount || 0) < 1));

  return (
    <AuctionBuyerLayout activeKey="promise">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 100px' }}>
        <button type="button" onClick={function(){navigate('/buyer/promise');}}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 0', marginBottom: 16, fontFamily: "'Pretendard',sans-serif" }}>
          ← PROMISE 목록
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 24, alignItems: 'start' }}>
          {/* 좌측 — 사진 + 정보 */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {photoUrls.length > 0 ? (
                <img src={photoUrls[photoIdx]} alt=""
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                />
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                  사진 없음
                </div>
              )}
              {photoUrls.length > 1 && (
                <div style={{ display: 'flex', gap: 4, padding: 8, overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                  {photoUrls.map(function(url, i){
                    return (
                      <button key={i} type="button" onClick={function(){setPhotoIdx(i);}}
                        style={{
                          flex: '0 0 80px', width: 80, height: 60, padding: 0,
                          border: photoIdx === i ? '2px solid #1B3A5C' : '1px solid #e2e8f0',
                          borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: 'white',
                        }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                {rfq.year ? rfq.year + ' ' : ''}{rfq.model || '차량'}
              </h2>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                {rfq.carNo && <span>차량번호: <strong style={{ color: '#0f172a' }}>{rfq.carNo}</strong></span>}
                {rfq.vin && <span>VIN: <strong style={{ color: '#0f172a' }}>{rfq.vin}</strong></span>}
                {rfq.mileage != null && <span>주행거리: <strong style={{ color: '#0f172a' }}>{Number(rfq.mileage).toLocaleString()} km</strong></span>}
                {rfq.fuel && <span>연료: <strong style={{ color: '#0f172a' }}>{rfq.fuel}</strong></span>}
                {rfq.color && <span>색상: <strong style={{ color: '#0f172a' }}>{rfq.color}</strong></span>}
              </div>
              {rfq.memo && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>출품자 메모</div>
                  <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{rfq.memo}</div>
                </div>
              )}
              {rfq.ownerConsented && (
                <div style={{ marginTop: 16, padding: 10, background: '#ecfdf5', borderRadius: 8, fontSize: 12, color: '#065f46', fontWeight: 600 }}>
                  ✓ 차주 동의 완료 — 매입 거래 시 본인 명의 처리 가능
                </div>
              )}
            </div>
          </div>

          {/* 우측 — 시세 sticky 카드 */}
          <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>현재 시세 제출 수</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1B3A5C', marginTop: 4 }}>
              {rfq.quoteCount || 0}<span style={{ fontSize: 16, color: '#94a3b8', marginLeft: 4 }}>개</span>
            </div>

            {hasMyQuote && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14, marginTop: 16 }}>
                <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600, marginBottom: 4 }}>내 시세</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af' }}>
                  {formatKrw(rfq.ownQuote.priceKrw)}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4 }}>
                  상태: {myQuoteStatus === 'active' ? '활성' : (myQuoteStatus === 'withdrawn' ? '철회됨' : (myQuoteStatus === 'committed' ? 'commit 됨' : myQuoteStatus))} · 수정 {rfq.ownQuote.updateCount || 0}/1
                </div>
                {rfq.ownQuote.memo && (
                  <div style={{ fontSize: 12, color: '#1e40af', marginTop: 8, paddingTop: 8, borderTop: '1px solid #bfdbfe' }}>{rfq.ownQuote.memo}</div>
                )}
              </div>
            )}

            {!isActive && (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 12, marginTop: 16, fontSize: 13, color: '#92400e' }}>
                {rfq.status === 'committed' ? '⏳ 출품자가 시세를 선택했습니다.' : (rfq.status === 'completed' ? '✓ 거래 완료된 PROMISE.' : '마감된 PROMISE 입니다.')}
              </div>
            )}

            <button type="button" onClick={function(){setQuoteModalOpen(true);}}
              disabled={!canQuote}
              style={{
                width: '100%', height: 52,
                background: canQuote ? 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)' : '#cbd5e1',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: canQuote ? 'pointer' : 'not-allowed',
                marginTop: 20, fontFamily: "'Pretendard',sans-serif",
                boxShadow: canQuote ? '0 4px 12px rgba(27,58,92,0.2)' : 'none',
              }}>
              {!isActive ? '마감됨' : (hasMyQuote ? (canQuote ? '시세 수정' : '수정 한도 도달') : '시세 제출')}
            </button>

            {hasMyQuote && myQuoteStatus === 'active' && isActive && (
              <button type="button" onClick={handleWithdraw}
                style={{
                  width: '100%', height: 40, marginTop: 10,
                  background: 'white', color: '#dc2626',
                  border: '1.5px solid #fecaca', borderRadius: 8,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Pretendard',sans-serif",
                }}>
                시세 철회
              </button>
            )}

            {rfq.sellerTier && rfq.sellerTier.label && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>출품자 등급</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: rfq.sellerTier.color || '#0f172a' }}>{rfq.sellerTier.label}</div>
                {rfq.sellerTier.completionRate != null && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    완료율 {Math.round(rfq.sellerTier.completionRate * 100)}% · 거래 {rfq.sellerTier.sampleSize || 0}건
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {quoteModalOpen && (
          <AuctionBuyerQuoteModal rfq={rfq}
            existingQuote={hasMyQuote && myQuoteStatus === 'active' ? rfq.ownQuote : null}
            onClose={function(){setQuoteModalOpen(false);}}
            onSuccess={function(){ load(); }}
          />
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerPromiseDetail;
